import React, { useState } from 'react';
import laboratoryInfo from '../config';
import { useData } from '../contexts/DataContext';
import { getLotNumbers, getApplicableStandardsText } from '../data/materialLotData';
import { FaTools, FaDownload } from 'react-icons/fa';
import DocumentPreview from './DocumentPreview';

const WorkSheetGenerator = ({ order, showButton = true, buttonLabel = "Fiche", className, icon }) => {
    const { products, clients } = useData();
    const practitionerClient = clients.find(c => c.id === (order?.clientId || order?.client_id));
    const [showPreview, setShowPreview] = useState(false);

    const items = [];
    const currentOrder = order || {};
    const currentId = currentOrder.id || 'preview';
    const targetId = `worksheet-img-${currentId}`;
    const fileName = `fiche-${currentOrder.patientName || 'travail'}`;

    if (currentOrder.teethConfig) {
        if (Array.isArray(currentOrder.teethConfig)) {
            // New Array Format
            currentOrder.teethConfig.forEach(line => {
                const { productId, teeth, details } = line;
                const product = products.find(p => p.id === productId);

                if (product) {
                    items.push({
                        toothId: teeth.map(t => t === 'HAUT' ? 'Haut' : t === 'BAS' ? 'Bas' : t).join(', '),
                        category: product.category,
                        name: product.name,
                        quantity: line.quantity !== undefined && line.quantity !== null && !isNaN(Number(line.quantity)) 
                            ? Number(line.quantity) 
                            : (teeth.length || 1),
                        details: details || {},
                        notes: line.notes
                    });
                }
            });
        } else {
            // Legacy Object Format
            Object.entries(currentOrder.teethConfig).forEach(([toothId, config]) => {
                const { productId, details } = config;
                const product = products.find(p => p.id === productId);

                if (product) {
                    items.push({
                        toothId,
                        category: product.category,
                        name: product.name,
                        quantity: 1,
                        details: details || {},
                        notes: config.notes
                    });
                }
            });
        }
    }

    // Calcul précis des lots et des normes applicables
    const detectedLots = [];
    const seenLotLabels = new Set();
    items.forEach(item => {
        const lots = getLotNumbers(item.category, item.name, item.details?.teinte, item.notes);
        lots.forEach(l => {
            if (!seenLotLabels.has(l.label)) {
                seenLotLabels.add(l.label);
                detectedLots.push(l);
            }
        });
    });

    return (
        <>
            {showButton && (
                <button
                    className={className || "btn btn-sm btn-outline-secondary"}
                    onClick={(e) => { e.stopPropagation(); setShowPreview(true); }}
                    title="Générer / Télécharger Fiche de travail"
                >
                    {icon !== undefined ? icon : (buttonLabel?.toLowerCase().includes('télécharger') ? <FaDownload className="me-1" /> : <FaTools className={buttonLabel ? "me-1" : ""} />)}
                    {buttonLabel}
                </button>
            )}

            {showPreview && (
                <DocumentPreview
                    targetId={targetId}
                    fileName={fileName}
                    onClose={() => setShowPreview(false)}
                />
            )}

            {/* Hidden Template for Image Capture */}
            <div id={targetId} style={{ display: 'none' }}>
                <div style={{ backgroundColor: 'white', padding: '20mm', color: 'black', width: '210mm', boxSizing: 'border-box', fontFamily: 'Arial, sans-serif' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '30px', borderBottom: '3px solid #1e293b', paddingBottom: '15px' }}>
                        <div>
                            <h2 style={{ margin: '0', color: '#1e293b' }}>{laboratoryInfo.name}</h2>
                            <p style={{ margin: '5px 0', fontSize: '13px', color: '#64748b', fontWeight: 'bold' }}>LABORATOIRE DE PROTHÈSE DENTAIRE</p>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                            <h1 style={{ margin: '0', fontSize: '24px', color: '#1e293b' }}>FICHE DE TRAVAIL</h1>
                            <p style={{ margin: '5px 0', fontWeight: 'bold', fontSize: '14px' }}>N° DOSSIER: {currentOrder.caseNumber || (typeof currentId === 'string' ? currentId.substring(0, 8).toUpperCase() : 'PREVIEW')}</p>
                        </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '30px' }}>
                        <div style={{ border: '1px solid #e2e8f0', padding: '15px', borderRadius: '8px' }}>
                            <h4 style={{ margin: '0 0 10px 0', backgroundColor: '#f8fafc', padding: '8px', fontSize: '14px', borderRadius: '4px', color: '#475569' }}>PRATICIEN</h4>
                            <p style={{ margin: '5px 0', fontSize: '14px' }}><strong>Nom:</strong> {currentOrder.practitionerName}</p>
                            {practitionerClient?.address && <p style={{ margin: '5px 0', fontSize: '13px', color: '#475569' }}><strong>Adresse:</strong> {practitionerClient.address}</p>}
                            <p style={{ margin: '5px 0', fontSize: '14px' }}><strong>Date commande:</strong> {new Date(currentOrder.createdAt || new Date()).toLocaleDateString()}</p>
                        </div>
                        <div style={{ border: '1px solid #e2e8f0', padding: '15px', borderRadius: '8px' }}>
                            <h4 style={{ margin: '0 0 10px 0', backgroundColor: '#f8fafc', padding: '8px', fontSize: '14px', borderRadius: '4px', color: '#475569' }}>PATIENT</h4>
                            <p style={{ margin: '5px 0', fontSize: '14px' }}><strong>Nom:</strong> {currentOrder.patientName}</p>
                            {(currentOrder.patientReference || currentOrder.patient_reference) && (
                                <p style={{ margin: '5px 0', fontSize: '14px' }}><strong>Référence :</strong> {currentOrder.patientReference || currentOrder.patient_reference}</p>
                            )}
                            <p style={{ margin: '5px 0', fontSize: '14px' }}><strong>Gamme:</strong> {currentOrder.gamme ? (currentOrder.gamme.toLowerCase() === 'nova' ? 'FINITION NOVA' : currentOrder.gamme.toUpperCase()) : 'FINITION NOVA'}</p>
                        </div>
                    </div>

                    {Boolean((currentOrder.notes && currentOrder.notes.includes('[REDO')) || items.some(it => Number(it.quantity) === 0)) && (
                        <div style={{ backgroundColor: '#fffbeb', border: '1.5px solid #f59e0b', borderRadius: '4px', padding: '8px 12px', margin: '15px 0', color: '#92400e', fontWeight: 'bold', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span>🔄 CAS À REFAIRE (REDO SOUS GARANTIE)</span>
                        </div>
                    )}

                    <h4 style={{ backgroundColor: '#1e293b', color: '#fff', padding: '10px', margin: '20px 0 10px 0', borderRadius: '4px', fontSize: '15px' }}>DÉTAILS TECHNIQUES</h4>

                    <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '30px' }}>
                        <thead>
                            <tr style={{ borderBottom: '2px solid #1e293b' }}>
                                <th style={{ textAlign: 'center', padding: '12px 4px', width: '140px', color: '#475569' }}>DENT</th>
                                <th style={{ textAlign: 'left', padding: '12px', color: '#475569' }}>TRAVAIL À RÉALISER</th>
                                <th style={{ textAlign: 'center', padding: '12px', width: '110px', color: '#475569' }}>QUANTITÉ</th>
                                <th style={{ textAlign: 'left', padding: '12px', color: '#475569' }}>TEINTE / NOTES</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((item, i) => (
                                <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                    <td style={{ textAlign: 'center', padding: '15px 4px', fontWeight: 'bold', fontSize: '14px', color: '#1e293b', lineHeight: '1.4' }}>{item.toothId}</td>
                                    <td style={{ padding: '15px' }}>
                                        <div style={{ fontWeight: 'bold', color: '#334155' }}>{item.category}</div>
                                        <div style={{ fontSize: '14px', color: '#475569' }}>{item.name}</div>
                                        {item.notes && (
                                            <div style={{ marginTop: '8px', padding: '6px 10px', backgroundColor: '#fef3c7', border: '1.5px solid #f59e0b', borderRadius: '4px', fontSize: '13px', fontWeight: 'bold', color: '#92400e' }}>
                                                ⚠️ NOTE : {item.notes}
                                            </div>
                                        )}
                                    </td>
                                    <td style={{ textAlign: 'center', padding: '15px', fontSize: '14px', fontWeight: 'bold' }}>
                                        {Number(item.quantity) === 0 ? (
                                            <span style={{ display: 'inline-block', backgroundColor: '#fef3c7', color: '#92400e', border: '1px solid #f59e0b', borderRadius: '4px', padding: '3px 6px', fontSize: '11px', whiteSpace: 'nowrap' }}>
                                                0 (À refaire)
                                            </span>
                                        ) : (
                                            item.quantity
                                        )}
                                    </td>
                                    <td style={{ padding: '15px' }}>
                                        {item.details.teinte && <div style={{ fontSize: '14px' }}><strong>Teinte:</strong> {item.details.teinte}</div>}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '30px' }}>
                        <div style={{ border: '1px solid #e2e8f0', padding: '15px', minHeight: '120px', borderRadius: '8px' }}>
                            <h4 style={{ margin: '0 0 10px 0', borderBottom: '1px solid #f1f5f9', paddingBottom: '8px', fontSize: '14px', color: '#475569' }}>NOTES & REMARQUES</h4>
                            {currentOrder.notes && (
                                <div style={{ fontSize: '13px', color: '#1e293b', marginBottom: '10px', backgroundColor: '#fffbeb', padding: '8px', borderRadius: '4px', borderLeft: '3px solid #f59e0b', whiteSpace: 'pre-wrap' }}>
                                    <strong>Remarques du cas :</strong> {currentOrder.notes}
                                </div>
                            )}
                            {items.filter(item => item.notes && item.notes.trim() !== '').length > 0 && (
                                <div style={{ fontSize: '14px', color: '#1e293b' }}>
                                    {items.filter(item => item.notes && item.notes.trim() !== '').map((item, idx) => (
                                        <div key={idx} style={{ marginBottom: '8px', padding: '6px 10px', backgroundColor: '#fef3c7', border: '1px solid #fde68a', borderRadius: '4px', borderLeft: '4px solid #f59e0b' }}>
                                            <strong style={{ color: '#b45309' }}>Dent(s) {item.toothId} :</strong> <span style={{ fontWeight: 'bold', color: '#1e293b' }}>{item.notes}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div style={{ border: '1px solid #cbd5e1', padding: '12px', borderRadius: '8px', backgroundColor: '#f8fafc' }}>
                            <h4 style={{ margin: '0 0 8px 0', borderBottom: '1px solid #cbd5e1', paddingBottom: '6px', fontSize: '13px', color: '#1e3a8a' }}>TRAÇABILITÉ DES MATÉRIAUX & NORMES ISO / UE</h4>
                            <div style={{ fontSize: '10.5px', color: '#334155', lineHeight: '1.8' }}>
                                {detectedLots.length > 0 ? (
                                    detectedLots.map((lot, idx) => (
                                        <div key={idx}><strong>{lot.label} :</strong> {lot.value}</div>
                                    ))
                                ) : (
                                    <>
                                        Lot Disque Zircone / Métal Co-Cr : ___________________________<br />
                                        Lot Résine / Porcelaine : ___________________________<br />
                                        Lot Dents résine / Titane : ___________________________<br />
                                    </>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Déclaration d'engagement et de conformité DM sur mesure */}
                    <div style={{ marginBottom: '20px', border: '1px solid #cbd5e1', padding: '10px 14px', borderRadius: '6px', backgroundColor: '#ffffff' }}>
                        <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#1e293b', marginBottom: '3px', textTransform: 'uppercase' }}>
                            Déclaration de Conformité — Dispositif Médical sur Mesure (DMSM)
                        </div>
                        <p style={{ margin: '0', fontSize: '10px', color: '#475569', lineHeight: '1.4', textAlign: 'justify' }}>
                            Le laboratoire <strong>{laboratoryInfo.name}</strong> certifie que ce dispositif médical sur mesure a été fabriqué exclusivement pour le patient susnommé conformément à la prescription médicale du praticien et répond aux exigences générales de sécurité et de performances du <strong>Règlement Européen (UE) 2017/745 (MDR)</strong> et {getApplicableStandardsText(detectedLots)}
                        </p>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'flex-start', marginTop: '25px', borderTop: '1px solid #e2e8f0', paddingTop: '15px' }}>
                        <div style={{ textAlign: 'center', border: '1px solid #94a3b8', borderRadius: '8px', padding: '6px 14px', fontSize: '11px', color: '#475569', minWidth: '150px' }}>
                            <strong>Contrôle Qualité de Libération</strong>
                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '35px', marginTop: '4px' }}>
                                <img src="/signature.png" alt="Signature" style={{ height: '45px', maxHeight: '45px', objectFit: 'contain' }} />
                            </div>
                        </div>
                    </div>

                    <div style={{
                        marginTop: '20px',
                        textAlign: 'center',
                        fontSize: '9.5px',
                        color: '#94a3b8',
                        borderTop: '1px solid #f1f5f9',
                        paddingTop: '8px'
                    }}>
                        <p style={{ fontWeight: 'bold', margin: '0' }}>{laboratoryInfo.fullName} - SIRET: {laboratoryInfo.siret} - TVA: {laboratoryInfo.tva}</p>
                    </div>
                </div>
            </div>
        </>
    );
};

export default WorkSheetGenerator;
